import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';
import { hrms } from '../../services/api';
import { theme } from '../../theme';

// ── Palette ──────────────────────────────────────────────────────────────────
const C = {
  blue:   '#3B82F6',
  green:  '#10B981',
  yellow: '#F59E0B',
  red:    '#EF4444',
  purple: '#8B5CF6',
  teal:   '#14B8A6',
  orange: '#F97316',
  gray:   '#6B7280',
};
const PIE_COLORS = [C.blue, C.green, C.yellow, C.red, C.purple, C.teal, C.orange, C.gray];

// ── Reusable primitives ───────────────────────────────────────────────────────
function KpiCard({ title, value, sub, color = C.blue, icon, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: '#fff', borderRadius: '0.75rem', padding: '1.1rem 1.25rem',
        border: `1px solid ${theme.border}`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        display: 'flex', flexDirection: 'column', gap: '0.3rem',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'box-shadow 0.15s',
      }}
      onMouseEnter={e => onClick && (e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)')}
      onMouseLeave={e => onClick && (e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)')}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <span style={{ fontSize: '0.72rem', fontWeight: 600, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{title}</span>
        <span style={{ fontSize: '1.3rem' }}>{icon}</span>
      </div>
      <span style={{ fontSize: '1.9rem', fontWeight: 800, color, lineHeight: 1 }}>{value ?? '—'}</span>
      {sub && <p style={{ fontSize: '0.75rem', color: theme.textMuted, margin: 0 }}>{sub}</p>}
      <div style={{ height: '3px', background: `linear-gradient(90deg, ${color}, transparent)`, borderRadius: 9999, marginTop: '0.2rem' }} />
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h2 style={{ fontSize: '1rem', fontWeight: 700, color: theme.text, margin: '0 0 0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      {children}
    </h2>
  );
}

function ChartCard({ title, children, height = 240, action }) {
  return (
    <div style={{ background: '#fff', borderRadius: '0.75rem', padding: '1.1rem 1.25rem', border: `1px solid ${theme.border}`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <p style={{ fontSize: '0.875rem', fontWeight: 700, color: theme.text, margin: 0 }}>{title}</p>
        {action}
      </div>
      <div style={{ height }}>{children}</div>
    </div>
  );
}

function ActionQueue({ label, count, color, icon, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: '0.85rem',
        padding: '0.8rem 1rem', borderRadius: '0.6rem',
        background: hovered ? (count > 0 ? `${color}22` : '#f1f5f9') : (count > 0 ? `${color}12` : theme.pageBg),
        border: `1px solid ${hovered ? color + '88' : (count > 0 ? color + '44' : theme.border)}`,
        cursor: 'pointer',
        transform: hovered ? 'translateX(2px)' : 'none',
        transition: 'all 0.15s ease',
        boxShadow: hovered ? `0 2px 8px ${color}22` : 'none',
      }}
    >
      <span style={{ fontSize: '1.2rem' }}>{icon}</span>
      <div style={{ flex: 1 }}>
        <p style={{ margin: 0, fontSize: '0.82rem', fontWeight: 600, color: hovered ? color : theme.text }}>{label}</p>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
        <span style={{
          minWidth: 28, height: 28, borderRadius: '50%',
          background: count > 0 ? color : theme.border,
          color: '#fff', fontSize: '0.8rem', fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>{count}</span>
        <span style={{ fontSize: '0.75rem', color: hovered ? color : theme.textMuted, fontWeight: 700, transition: 'all 0.15s' }}>→</span>
      </div>
    </div>
  );
}

function ListRow({ primary, secondary, badge, badgeColor = C.yellow }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '0.6rem 0', borderBottom: `1px solid ${theme.border}`,
    }}>
      <div>
        <p style={{ margin: 0, fontSize: '0.83rem', fontWeight: 600, color: theme.text }}>{primary}</p>
        {secondary && <p style={{ margin: '0.1rem 0 0', fontSize: '0.73rem', color: theme.textMuted }}>{secondary}</p>}
      </div>
      {badge && (
        <span style={{
          fontSize: '0.7rem', fontWeight: 700, padding: '0.2rem 0.6rem',
          borderRadius: 999, background: `${badgeColor}22`, color: badgeColor,
          whiteSpace: 'nowrap', marginLeft: '0.75rem',
        }}>{badge}</span>
      )}
    </div>
  );
}

const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—';
const fmtDateFull = d => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

// ── Main Component ────────────────────────────────────────────────────────────
export default function HRDashboard() {
  const navigate = useNavigate();

  const [employees,        setEmployees]        = useState([]);
  const [departments,      setDepartments]      = useState([]);
  const [pendingLeaves,    setPendingLeaves]     = useState([]);
  const [pendingWFH,       setPendingWFH]        = useState([]);
  const [candidates,       setCandidates]        = useState([]);
  const [requisitions,     setRequisitions]      = useState([]);
  const [trainings,        setTrainings]         = useState([]);
  const [perfReviews,      setPerfReviews]       = useState([]);
  const [leaveRequests,    setLeaveRequests]     = useState([]);
  const [todayAttendance,  setTodayAttendance]   = useState(null);
  const [loading,          setLoading]           = useState(true);
  const [attModal,         setAttModal]          = useState(null); // { status, label, color }
  const [attEmployees,     setAttEmployees]      = useState([]);
  const [attLoading,       setAttLoading]        = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const results = await Promise.allSettled([
      hrms.getEmployees({ limit: 2000 }),
      hrms.getDepartments(),
      hrms.getLeaveRequests({ status: 'pending', limit: 100 }),
      hrms.getWFHRequests({ status: 'pending', limit: 100 }),
      hrms.getCandidates({ limit: 200 }),
      hrms.getRequisitions({ status: 'open', limit: 100 }),
      hrms.getTrainingPrograms({ limit: 50 }),
      hrms.getPerformanceReviews({ limit: 500 }),
      hrms.getLeaveRequests({ limit: 10 }),
      hrms.getAttendanceSummaryToday(),
    ]);

    const ok = (r, fallback = []) => r.status === 'fulfilled' ? (r.value?.data ?? fallback) : fallback;
    const toArr = v => Array.isArray(v) ? v : (v?.items || v?.employees || v?.data || []);

    setEmployees(toArr(ok(results[0], [])));
    setDepartments(toArr(ok(results[1], [])));
    setPendingLeaves(toArr(ok(results[2], [])));
    setPendingWFH(toArr(ok(results[3], [])));
    setCandidates(toArr(ok(results[4], [])));
    setRequisitions(toArr(ok(results[5], [])));
    setTrainings(toArr(ok(results[6], [])));
    setPerfReviews(toArr(ok(results[7], [])));
    setLeaveRequests(toArr(ok(results[8], [])));
    if (results[9].status === 'fulfilled') setTodayAttendance(results[9].value?.data || null);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 360, flexDirection: 'column', gap: '1rem' }}>
      <div style={{ width: 44, height: 44, border: `4px solid ${theme.border}`, borderTop: `4px solid ${theme.primary}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <p style={{ color: theme.textMuted, fontSize: '0.875rem' }}>Loading HR dashboard…</p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  // ── Derived data ─────────────────────────────────────────────────────────────
  const now = new Date();
  const activeEmps = employees.filter(e => (e.employment_status || e.status) === 'active');

  const openAttModal = async (status, label, color) => {
    setAttModal({ status, label, color });
    setAttEmployees([]);
    setAttLoading(true);
    try {
      const r = await hrms.getAttendanceTodayEmployees(status);
      setAttEmployees(r.data || []);
    } catch { setAttEmployees([]); }
    finally { setAttLoading(false); }
  };

  // Headcount by department
  const deptData = departments.map(d => ({
    name: d.name?.length > 14 ? d.name.slice(0, 14) + '…' : d.name,
    count: employees.filter(e => e.department_id === d.id && (e.employment_status || e.status) === 'active').length,
  })).filter(d => d.count > 0).sort((a, b) => b.count - a.count);

  // Employment type pie
  const typeCounts = activeEmps.reduce((acc, e) => {
    const t = e.employment_type || 'full_time';
    acc[t] = (acc[t] || 0) + 1;
    return acc;
  }, {});
  const typeLabels = { full_time: 'Full-time', part_time: 'Part-time', contract: 'Contract', intern: 'Intern' };
  const empTypeData = Object.entries(typeCounts).map(([k, v]) => ({ name: typeLabels[k] || k, value: v }));

  // Leave analytics — breakdown by type
  const leaveTypeCounts = leaveRequests.reduce((acc, r) => {
    const t = r.leave_type_name || r.leave_type?.name || 'Other';
    acc[t] = (acc[t] || 0) + 1;
    return acc;
  }, {});
  const leaveTypeData = Object.entries(leaveTypeCounts)
    .map(([k, v]) => ({ name: k, count: v }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 8);

  // Leave status breakdown
  const leaveStatusData = [
    { name: 'Approved', count: leaveRequests.filter(r => r.status === 'approved').length, fill: C.green },
    { name: 'Pending',  count: leaveRequests.filter(r => r.status === 'pending').length,  fill: C.yellow },
    { name: 'Rejected', count: leaveRequests.filter(r => r.status === 'rejected').length, fill: C.red },
  ].filter(d => d.count > 0);

  // Recruitment — candidates by stage
  const stageCounts = candidates.reduce((acc, c) => {
    const s = c.stage || c.status || 'applied';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});
  const stageOrder = ['applied', 'shortlisted', 'interview_scheduled', 'interview_done', 'offered', 'joined', 'rejected'];
  const stageLabels = { applied: 'Applied', shortlisted: 'Shortlisted', interview_scheduled: 'Scheduled', interview_done: 'Interviewed', offered: 'Offered', joined: 'Joined', rejected: 'Rejected' };
  const candidateStageData = stageOrder
    .filter(s => stageCounts[s] > 0)
    .map(s => ({ name: stageLabels[s] || s, count: stageCounts[s] }));

  // Joiners this month
  const joinersThisMonth = activeEmps.filter(e => {
    const d = new Date(e.hire_date || e.created_at);
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  });

  // Upcoming probation completions (within 30 days)
  const probationDue = activeEmps.filter(e => {
    if (!e.hire_date) return false;
    const hire = new Date(e.hire_date);
    const probEnd = new Date(hire);
    probEnd.setMonth(probEnd.getMonth() + 6);
    const diff = (probEnd - now) / (1000 * 60 * 60 * 24);
    return diff >= 0 && diff <= 30;
  }).slice(0, 5);

  // Tenure distribution
  const tenureBuckets = { '< 1 Year': 0, '1 – 3 Years': 0, '3 – 5 Years': 0, '5+ Years': 0 };
  activeEmps.forEach(e => {
    if (!e.hire_date) return;
    const yrs = (now - new Date(e.hire_date)) / (1000 * 60 * 60 * 24 * 365);
    if (yrs < 1)      tenureBuckets['< 1 Year']++;
    else if (yrs < 3) tenureBuckets['1 – 3 Years']++;
    else if (yrs < 5) tenureBuckets['3 – 5 Years']++;
    else              tenureBuckets['5+ Years']++;
  });
  const tenureData = Object.entries(tenureBuckets).map(([k, v]) => ({ name: k, count: v }));

  // Training status
  const trainingStatus = trainings.reduce((acc, t) => {
    const s = t.status || 'planned';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});
  const upcomingTrainings = trainings
    .filter(t => t.start_date && new Date(t.start_date) >= now)
    .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
    .slice(0, 5);

  // Performance cycle
  const reviewPending   = perfReviews.filter(r => !r.overall_rating).length;
  const reviewCompleted = perfReviews.filter(r => r.overall_rating).length;

  return (
    <>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.75rem' }}>

      {/* ── Header ── */}
      <div style={{
        background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a8e 100%)',
        borderRadius: '0.875rem', padding: '1.5rem 2rem',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem',
      }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#F8FAFC', margin: 0 }}>HR Admin Dashboard</h1>
          <p style={{ color: '#94A3B8', fontSize: '0.875rem', margin: '0.25rem 0 0' }}>
            {new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
        <button onClick={load} style={{ padding: '0.55rem 1.25rem', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: '#F8FAFC', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600 }}>
          ↻ Refresh
        </button>
      </div>

      {/* ── Today's Attendance ── */}
      <div>
        <SectionTitle>🕐 Today's Attendance
          <span style={{ fontSize: '0.75rem', fontWeight: 400, color: theme.textMuted, marginLeft: 6 }}>
            {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
          </span>
        </SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '1rem' }}>
          {[
            {
              title: 'Present',
              value: todayAttendance?.present ?? '—',
              sub: todayAttendance ? `of ${todayAttendance.total_active} active` : 'Loading…',
              color: C.green, icon: '✅', status: 'present',
            },
            {
              title: 'Work From Home',
              value: todayAttendance?.wfh ?? '—',
              sub: 'Approved WFH today',
              color: C.blue, icon: '🏠', status: 'wfh',
            },
            {
              title: 'On Leave',
              value: todayAttendance?.on_leave ?? '—',
              sub: 'Approved leaves today',
              color: C.yellow, icon: '📅', status: 'on_leave',
            },
            {
              title: 'Absent / Not Marked',
              value: todayAttendance?.absent ?? '—',
              sub: 'No record or approval',
              color: todayAttendance?.absent > 0 ? C.red : C.gray, icon: '❌', status: 'absent',
            },
          ].map(card => (
            <KpiCard key={card.title} title={card.title} value={card.value}
              sub={card.sub} color={card.color} icon={card.icon}
              onClick={() => openAttModal(card.status, card.title, card.color)} />
          ))}
        </div>
      </div>

      {/* ── Section 1: KPI Strip ── */}
      <div>
        <SectionTitle>📊 Workforce Overview</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '1rem' }}>
          <KpiCard title="Total Active" value={activeEmps.length} sub={`${employees.length} total on record`} color={C.blue} icon="👥" />
          <KpiCard title="Leaves Pending" value={pendingLeaves.length} sub="Awaiting HR approval" color={pendingLeaves.length > 5 ? C.red : C.yellow} icon="📅" onClick={() => navigate('/hrms/leave')} />
          <KpiCard title="WFH Pending" value={pendingWFH.length} sub="Awaiting HR approval" color={pendingWFH.length > 5 ? C.orange : C.teal} icon="🏠" onClick={() => navigate('/hrms/wfh')} />
          <KpiCard title="Open Positions" value={requisitions.length} sub={`${candidates.length} candidates in pipeline`} color={C.purple} icon="💼" onClick={() => navigate('/hrms/interviews')} />
          <KpiCard title="New Joiners" value={joinersThisMonth.length} sub="This month" color={C.green} icon="🎉" />
          <KpiCard title="Trainings Upcoming" value={upcomingTrainings.length} sub="Scheduled programs" color={C.teal} icon="📚" onClick={() => navigate('/hrms/training')} />
        </div>
      </div>

      {/* ── Section 2: Action Queue + Headcount ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: '1.5rem', alignItems: 'start' }}>

        {/* Action Queue */}
        <div style={{ background: '#fff', borderRadius: '0.75rem', padding: '1.1rem 1.25rem', border: `1px solid ${theme.border}`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          <p style={{ fontSize: '0.875rem', fontWeight: 700, color: theme.text, margin: '0 0 0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            ⚡ Today's Action Queue
            {(pendingLeaves.length + pendingWFH.length) > 0 && (
              <span style={{ marginLeft: 'auto', background: C.red, color: '#fff', fontSize: '0.7rem', fontWeight: 700, padding: '0.15rem 0.5rem', borderRadius: 999 }}>
                {pendingLeaves.length + pendingWFH.length} pending
              </span>
            )}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            <ActionQueue label="Leave Requests" count={pendingLeaves.length} color={C.yellow} icon="📅" onClick={() => navigate('/hrms/leave', { state: { filterStatus: 'pending' } })} />
            <ActionQueue label="WFH Requests" count={pendingWFH.length} color={C.teal} icon="🏠" onClick={() => navigate('/hrms/wfh', { state: { filterStatus: 'pending' } })} />
            <ActionQueue label="Open Requisitions" count={requisitions.length} color={C.purple} icon="💼" onClick={() => navigate('/hrms/interviews', { state: { tab: 'candidates' } })} />
            <ActionQueue label="New Joiners (Month)" count={joinersThisMonth.length} color={C.green} icon="🎉" onClick={() => navigate('/hrms/employees?filter=new_joiners')} />
            <ActionQueue label="Pending Reviews" count={reviewPending} color={C.orange} icon="⭐" onClick={() => navigate('/hrms/performance')} />
            <ActionQueue label="Probation Due (30d)" count={probationDue.length} color={C.red} icon="⏰" onClick={() => navigate('/hrms/employees?filter=probation_due')} />
          </div>
        </div>

        {/* Headcount by Department */}
        <ChartCard title="Headcount by Department" height={Math.max(220, deptData.length * 38)}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={deptData} layout="vertical" margin={{ top: 0, right: 30, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.border} horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: '0.5rem' }} />
              <Bar dataKey="count" name="Employees" radius={[0, 4, 4, 0]}>
                {deptData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* ── Section 3: Leave Analytics ── */}
      <div>
        <SectionTitle>📅 Leave Analytics</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
          <ChartCard title="Leave Requests by Type">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={leaveTypeData} margin={{ top: 4, right: 8, bottom: 32, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-20} textAnchor="end" interval={0} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: '0.5rem' }} />
                <Bar dataKey="count" name="Requests" fill={theme.primary} radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Leave Approval Status">
            {leaveStatusData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={leaveStatusData} cx="50%" cy="45%" innerRadius={60} outerRadius={88} dataKey="count" paddingAngle={3}>
                    {leaveStatusData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: '0.5rem' }} />
                  <Legend iconSize={10} wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: theme.textMuted, fontSize: '0.875rem' }}>No leave data</div>
            )}
          </ChartCard>
        </div>
      </div>

      {/* ── Section 4: Recruitment Pipeline ── */}
      <div>
        <SectionTitle>💼 Recruitment Pipeline</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: '1.5rem', alignItems: 'start' }}>

          <ChartCard title="Candidates by Stage">
            {candidateStageData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={candidateStageData} margin={{ top: 4, right: 8, bottom: 0, left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: '0.5rem' }} />
                  <Bar dataKey="count" name="Candidates" radius={[4,4,0,0]}>
                    {candidateStageData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: theme.textMuted, fontSize: '0.875rem' }}>No candidates in pipeline</div>
            )}
          </ChartCard>

          {/* Open Requisitions */}
          <div style={{ background: '#fff', borderRadius: '0.75rem', padding: '1.1rem 1.25rem', border: `1px solid ${theme.border}`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <p style={{ fontSize: '0.875rem', fontWeight: 700, color: theme.text, margin: '0 0 0.75rem' }}>Open Positions ({requisitions.length})</p>
            {requisitions.length === 0 ? (
              <p style={{ color: theme.textMuted, fontSize: '0.82rem', textAlign: 'center', padding: '1rem 0' }}>No open requisitions</p>
            ) : (
              <div style={{ maxHeight: 240, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {requisitions.slice(0, 8).map(r => (
                  <div key={r.id} style={{ padding: '0.55rem 0.75rem', background: theme.pageBg, borderRadius: '0.5rem', border: `1px solid ${theme.border}` }}>
                    <p style={{ margin: 0, fontSize: '0.8rem', fontWeight: 600, color: theme.text }}>{r.title || r.job_title || 'Position'}</p>
                    <p style={{ margin: '0.1rem 0 0', fontSize: '0.72rem', color: theme.textMuted }}>{r.department_name || r.department || '—'} · {r.employment_type || 'Full-time'}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Section 5: New Joiners + Tenure ── */}
      <div>
        <SectionTitle>🎉 New Joiners & Workforce Tenure</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>

          {/* New Joiners this month */}
          <div style={{ background: '#fff', borderRadius: '0.75rem', padding: '1.1rem 1.25rem', border: `1px solid ${theme.border}`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <p style={{ fontSize: '0.875rem', fontWeight: 700, color: theme.text, margin: '0 0 0.75rem' }}>
              New Joiners This Month
              <span style={{ marginLeft: 8, background: `${C.green}22`, color: C.green, fontSize: '0.72rem', fontWeight: 700, padding: '0.15rem 0.5rem', borderRadius: 999 }}>{joinersThisMonth.length}</span>
            </p>
            {joinersThisMonth.length === 0 ? (
              <p style={{ color: theme.textMuted, fontSize: '0.82rem', textAlign: 'center', padding: '1rem 0' }}>No new joiners this month</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {joinersThisMonth.slice(0, 6).map(e => (
                  <ListRow
                    key={e.id}
                    primary={e.full_name || e.name}
                    secondary={`${e.designation || e.role || '—'} · Joined ${fmtDate(e.hire_date)}`}
                    badge={e.is_profile_complete === false ? 'Profile Incomplete' : 'Active'}
                    badgeColor={e.is_profile_complete === false ? C.red : C.green}
                  />
                ))}
                {joinersThisMonth.length > 6 && (
                  <p style={{ fontSize: '0.75rem', color: theme.textMuted, margin: '0.5rem 0 0', textAlign: 'right' }}>+{joinersThisMonth.length - 6} more</p>
                )}
              </div>
            )}

            {probationDue.length > 0 && (
              <>
                <p style={{ fontSize: '0.8rem', fontWeight: 700, color: C.orange, margin: '1rem 0 0.5rem', display: 'flex', alignItems: 'center', gap: 4 }}>⏰ Probation Ending Soon</p>
                {probationDue.map(e => {
                  const probEnd = new Date(e.hire_date);
                  probEnd.setMonth(probEnd.getMonth() + 6);
                  return (
                    <ListRow key={e.id} primary={e.full_name || e.name} secondary={`Hired ${fmtDate(e.hire_date)}`} badge={`Ends ${fmtDate(probEnd)}`} badgeColor={C.orange} />
                  );
                })}
              </>
            )}
          </div>

          {/* Tenure Distribution */}
          <ChartCard title="Employee Tenure Distribution">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={tenureData} margin={{ top: 4, right: 16, bottom: 0, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: '0.5rem' }} />
                <Bar dataKey="count" name="Employees" radius={[4,4,0,0]}>
                  {tenureData.map((_, i) => <Cell key={i} fill={[C.blue, C.green, C.yellow, C.purple][i]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </div>

      {/* ── Section 6: Employment Type + Trainings ── */}
      <div>
        <SectionTitle>📚 Workforce Mix & Training</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>

          <ChartCard title="Employment Type Breakdown">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={empTypeData} cx="50%" cy="45%" outerRadius={88} dataKey="value" label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`} labelLine={false}>
                  {empTypeData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Legend iconSize={10} wrapperStyle={{ fontSize: 12 }} />
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Upcoming Trainings */}
          <div style={{ background: '#fff', borderRadius: '0.75rem', padding: '1.1rem 1.25rem', border: `1px solid ${theme.border}`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <p style={{ fontSize: '0.875rem', fontWeight: 700, color: theme.text, margin: 0 }}>Upcoming Training Sessions</p>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {Object.entries(trainingStatus).map(([s, n]) => (
                  <span key={s} style={{ fontSize: '0.7rem', fontWeight: 600, padding: '0.15rem 0.5rem', borderRadius: 999, background: `${C.teal}22`, color: C.teal }}>{s}: {n}</span>
                ))}
              </div>
            </div>
            {upcomingTrainings.length === 0 ? (
              <p style={{ color: theme.textMuted, fontSize: '0.82rem', textAlign: 'center', padding: '1rem 0' }}>No upcoming trainings scheduled</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {upcomingTrainings.map(t => (
                  <ListRow
                    key={t.id}
                    primary={t.title || t.name}
                    secondary={`${fmtDateFull(t.start_date)}${t.trainer ? ` · ${t.trainer}` : ''}${t.max_participants ? ` · Max ${t.max_participants}` : ''}`}
                    badge={t.status || 'planned'}
                    badgeColor={t.status === 'active' ? C.green : C.teal}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Section 7: Performance Cycle Status ── */}
      <div>
        <SectionTitle>⭐ Performance Cycle Status</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1rem' }}>
          <KpiCard title="Reviews Completed" value={reviewCompleted} sub="With overall rating" color={C.green} icon="✅" />
          <KpiCard title="Reviews Pending" value={reviewPending} sub="Awaiting submission" color={reviewPending > 5 ? C.red : C.orange} icon="⏳" />
          <KpiCard title="Total Reviewed" value={perfReviews.length} sub="All review records" color={C.blue} icon="📋" />
          <KpiCard
            title="Avg Rating"
            value={perfReviews.filter(r => r.overall_rating).length > 0
              ? (perfReviews.filter(r => r.overall_rating).reduce((s, r) => s + r.overall_rating, 0) / perfReviews.filter(r => r.overall_rating).length).toFixed(1)
              : '—'}
            sub="Out of 5.0"
            color={C.purple}
            icon="⭐"
          />
        </div>
      </div>

      {/* Footer */}
      <p style={{ textAlign: 'center', fontSize: '0.75rem', color: theme.textMuted, paddingBottom: '0.5rem' }}>
        BonTon HRMS · HR Admin View · Data as of {new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
      </p>

    </div>

    {/* ── Attendance Drill-down Modal ── */}
    {attModal && (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
           onClick={e => e.target === e.currentTarget && setAttModal(null)}>
        <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 640,
                      maxHeight: '80vh', display: 'flex', flexDirection: 'column',
                      boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>

          {/* Header */}
          <div style={{ padding: '1.25rem 1.5rem', borderBottom: `1px solid ${theme.border}`,
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: attModal.color }} />
              <div>
                <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>{attModal.label}</h2>
                <p style={{ margin: 0, fontSize: '0.78rem', color: theme.textMuted }}>
                  {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  {!attLoading && ` · ${attEmployees.length} employee${attEmployees.length !== 1 ? 's' : ''}`}
                </p>
              </div>
            </div>
            <button onClick={() => setAttModal(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: '#9CA3AF' }}>×</button>
          </div>

          {/* Body */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {attLoading ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: theme.textMuted }}>Loading…</div>
            ) : attEmployees.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: theme.textMuted }}>
                No employees in this category today
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ background: '#F8FAFC' }}>
                    {['Emp Code', 'Name', 'Department', 'Designation'].map(h => (
                      <th key={h} style={{ padding: '0.65rem 1rem', textAlign: 'left', fontWeight: 700,
                                          color: theme.textMuted, borderBottom: `2px solid ${theme.border}`,
                                          fontSize: '0.72rem', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {attEmployees.map((emp, i) => (
                    <tr key={emp.id} style={{ borderBottom: `1px solid ${theme.border}` }}
                        onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '0.65rem 1rem', whiteSpace: 'nowrap' }}>
                        <span style={{ fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 700,
                                       background: `${attModal.color}18`, color: attModal.color,
                                       padding: '2px 8px', borderRadius: 4 }}>
                          {emp.emp_code}
                        </span>
                      </td>
                      <td style={{ padding: '0.65rem 1rem', fontWeight: 600 }}>{emp.full_name}</td>
                      <td style={{ padding: '0.65rem 1rem', color: theme.textMuted, fontSize: '0.8rem' }}>
                        {emp.department || '—'}
                      </td>
                      <td style={{ padding: '0.65rem 1rem', color: theme.textMuted, fontSize: '0.8rem' }}>
                        {emp.designation || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Footer */}
          <div style={{ padding: '0.875rem 1.5rem', borderTop: `1px solid ${theme.border}`,
                        display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={() => setAttModal(null)}
              style={{ padding: '0.5rem 1.5rem', border: 'none', borderRadius: 7,
                       background: attModal.color, color: '#fff', cursor: 'pointer',
                       fontWeight: 700, fontSize: '0.875rem' }}>
              Close
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
